import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CUST_REQ_KEY, useCustReqList } from '@indipia/shared-features';
import type { CustReqItem } from '@indipia/shared-features';
import type { CustReqOwnerApplyInput } from '../../types/custReqReport.types';
import {
  buildOwnerApplyInputs, ownerTargets, resolveOwnerTaskId, type OwnerChecks,
} from './custReqReport.logic';

/**
 * 사장님 직접 작성(C-2)의 고객 요청 상태 — 공정이 확정될 때만 요청을 조회·표시한다(확정결정 25).
 * 공정 확정 규칙은 resolveOwnerTaskId, 반영 대상은 그 공정에 전달돼 아직 반영되지 않은 요청이다.
 */

export interface OwnerCustReq {
  /** 영역 표시 여부 — 공정 확정 + 반영 대상 1건 이상 */
  visible: boolean;
  /** 공정은 확정됐지만 요청 조회가 실패함(오류 줄 표시용) */
  loadFailed: boolean;
  /** 영역을 보일 때만 값이 있는 확정 공정 ID — 저장 요청의 taskId 로 보낸다 */
  taskId: number | null;
  targets: CustReqItem[];
  checks: OwnerChecks;
  setApplied: (custReqId: number, applied: boolean) => void;
  /** 저장 요청에 실을 반영 입력 — 영역이 숨겨져 있으면 빈 배열 */
  buildInputs: () => CustReqOwnerApplyInput[];
  reset: () => void;
  /** 저장 성공 후 요청 캐시 무효화 */
  afterSave: () => void;
}

export function useOwnerCustReq(args: {
  selectedCodes: readonly string[];
  scheduleWorkTypes: ReadonlyArray<{ taskId: number; workTypeCode: string }> | undefined | null;
}): OwnerCustReq {
  const { selectedCodes, scheduleWorkTypes } = args;
  const qc = useQueryClient();
  const confirmedTaskId = useMemo(
    () => resolveOwnerTaskId(selectedCodes, scheduleWorkTypes),
    [selectedCodes, scheduleWorkTypes],
  );
  const query = useCustReqList(confirmedTaskId);
  const targets = useMemo(() => ownerTargets(query.data ?? []), [query.data]);
  const [checks, setChecks] = useState<OwnerChecks>({});

  // 공정이 바뀌면 입력 초기화
  useEffect(() => { setChecks({}); }, [confirmedTaskId]);

  const visible = confirmedTaskId != null && targets.length > 0;
  const loadFailed = confirmedTaskId != null && query.isError;

  const setApplied = useCallback((custReqId: number, applied: boolean) => {
    setChecks((prev) => ({ ...prev, [custReqId]: applied }));
  }, []);

  const buildInputs = useCallback(
    () => (visible ? buildOwnerApplyInputs(targets, checks) : []),
    [visible, targets, checks],
  );
  const reset = useCallback(() => setChecks({}), []);
  const afterSave = useCallback(() => { void qc.invalidateQueries({ queryKey: [CUST_REQ_KEY] }); }, [qc]);

  return {
    visible, loadFailed, taskId: visible ? confirmedTaskId : null, targets, checks,
    setApplied, buildInputs, reset, afterSave,
  };
}
